import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { StripedDataGrid } from '../../styledComponents/datagrid';
import { GridColDef, GridPaginationModel, GridRowParams } from '@mui/x-data-grid';
import { Alert, Box, Chip, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { PageWrapper } from '../../styledComponents/wrappers';
import { FormatDateString } from '../../utils/helper';
import {
  FulfillmentWaveStatus,
  FulfillmentWaveType,
  WaveListItem,
} from '../../services/fulfillment/types';
import {
  FULFILLMENT_PAGE_SIZE,
  WAVE_STATUS_COLORS,
  WAVE_STATUS_LABELS,
  WAVE_TYPE_LABELS,
} from '../../services/fulfillment/constants';
import { listWaves } from '../../services/fulfillment/waveApi';

export const WAVES_LIST_ROUTE = '/fulfillment/waves';
export const WAVE_DETAIL_ROUTE = '/fulfillment/waves/:waveId';

type StatusTab = 'all' | FulfillmentWaveStatus;

const columns: GridColDef[] = [
  {
    headerName: 'Wave ID',
    field: 'waveId',
    flex: 0.4,
    type: 'number',
  },
  {
    headerName: 'Type',
    field: 'type',
    flex: 0.8,
    valueFormatter: ({ value }) => WAVE_TYPE_LABELS[value as FulfillmentWaveType] ?? value,
  },
  {
    headerName: 'Status',
    field: 'status',
    flex: 0.8,
    renderCell: params => {
      const s = params.value as FulfillmentWaveStatus;
      const { bg, color } = WAVE_STATUS_COLORS[s] ?? { bg: '#eee', color: '#333' };
      return (
        <Chip
          label={WAVE_STATUS_LABELS[s] ?? s}
          size="small"
          sx={{ bgcolor: bg, color, fontWeight: 600, border: 'none' }}
        />
      );
    },
  },
  {
    headerName: 'Assigned To',
    field: 'assignedToName',
    flex: 0.8,
    valueFormatter: ({ value }) => value ?? '—',
  },
  {
    headerName: 'Orders',
    field: 'totalOrders',
    flex: 0.5,
    type: 'number',
    renderCell: params => {
      const row = params.row as WaveListItem;
      return (
        <Typography variant="body2">
          {row.completedOrders}/{row.totalOrders}
        </Typography>
      );
    },
  },
  {
    headerName: 'Created',
    field: 'created',
    flex: 0.8,
    valueFormatter: ({ value }) => (value ? FormatDateString(value) : ''),
  },
];

const WavesList: React.FC = () => {
  const history = useHistory();
  const [waves, setWaves] = useState<WaveListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: FULFILLMENT_PAGE_SIZE,
  });

  useEffect(() => {
    const fetchWaves = async () => {
      setLoading(true);
      setError(null);
      try {
        setWaves(await listWaves());
      } catch {
        setError('Failed to load waves.');
      } finally {
        setLoading(false);
      }
    };
    fetchWaves();
  }, []);

  const filtered = useMemo(() => {
    if (statusTab === 'all') return waves;
    return waves.filter(w => w.status === statusTab);
  }, [waves, statusTab]);

  const handleRowClick = (params: GridRowParams) => {
    history.push(`/fulfillment/waves/${(params.row as WaveListItem).waveId}`);
  };

  return (
    <PageWrapper>
      <Typography variant="h5" gutterBottom>
        Waves
      </Typography>
      <Tabs
        value={statusTab}
        onChange={(_, val) => {
          setStatusTab(val);
          setPaginationModel(m => ({ ...m, page: 0 }));
        }}
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="All" value="all" />
        <Tab label="Active" value="in_progress" />
        <Tab label="Completed" value="completed" />
        <Tab label="Cancelled" value="cancelled" />
      </Tabs>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <StripedDataGrid
          rows={filtered}
          columns={columns}
          getRowId={row => row.waveId}
          onRowClick={handleRowClick}
          autoHeight
          paginationModel={paginationModel}
          onPaginationModelChange={model => setPaginationModel(model)}
          pageSizeOptions={[FULFILLMENT_PAGE_SIZE, FULFILLMENT_PAGE_SIZE * 2]}
          initialState={{
            sorting: { sortModel: [{ field: 'created', sort: 'desc' }] },
            pagination: { paginationModel: { pageSize: FULFILLMENT_PAGE_SIZE } },
          }}
          sx={{ cursor: 'pointer' }}
          rowHeight={52}
        />
      )}
    </PageWrapper>
  );
};

export default WavesList;
